import React, { FC, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { TextInput } from "@trane/trane-components/dist";
import {
  DARK_GREY,
  INPUT_BACKGROUND,
  PRIMARY_BLUE,
} from "@trane/trane-components/dist/color";
import { Dashboard } from "./Dashboard";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { createTheme, styled, ThemeProvider } from "@mui/material/styles";
import Button, { ButtonProps } from "@mui/material/Button";
import {
  ButtonSize,
  ButtonTheme,
} from "@trane/trane-components/dist/simple/button";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  gridClasses,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import { CustomPagination } from "./CustomPagination";
import { useDebounce } from "use-debounce";

export interface DashboardTableProps {
  rows: Array<Dashboard>;
  labels: any;
  deleteDashboard: Function;
}

export const ButtonStyles = styled(Button)({
  background: `${ButtonTheme.primary.light}`,
  borderRadius: "24px",
  height: `${ButtonSize["default"].height}`,
  minWidth: `${ButtonSize["large"].width}`,
  fontFamily: "arial",
  width: "auto",
  color: `${ButtonTheme["primary"]["light"]["enabled"].color}`,
  border: `1px solid ${ButtonTheme["primary"]["light"]["enabled"].border}`,
  fontSize: `${ButtonSize["large"].fontSize}`,
  boxShadow: "rgba(0,0,0,.3) 0px 2px 4px",
  cursor: "pointer",
  padding: `${ButtonSize["large"].padding}`,
  opacity: `${ButtonTheme["primary"]["light"]["enabled"].opacity}`,
  "&:hover": {
    background: `${ButtonTheme["primary"]["light"]["enabled"].hoverBackground}`,
    color: `${ButtonTheme["primary"]["light"]["enabled"].hoverColor}`,
    border: ` 1px solid ${ButtonTheme["primary"]["light"]["enabled"].hoverBorder}`,
  },
});

const InputField = {
  background: `${INPUT_BACKGROUND}`,
  borderRadius: "6px",
  fontFamily: "ArialMT",
  width: "calc(100% - 48px)",
  height: "36px",
  color: `${DARK_GREY}`,
  border: `1px solid ${PRIMARY_BLUE}`,
  fontSize: `16px`,
  boxShadow: "0px 0px 5px 0px rgba(15, 51, 95, 0.2)",
  margin: "0",
  letterSpacing: "0.32px",
  outline: "none",
  padding: "0",
  "&:hover": {
    background: `${INPUT_BACKGROUND}`,
    color: `${DARK_GREY}`,
    border: `${PRIMARY_BLUE}`,
  },
};

const themeChange = createTheme({
  palette: {
    primary: {
      // light: will be calculated from palette.primary.main,
      main: "#e6efff",
      // dark: will be calculated from palette.primary.main,
      // contrastText: will be calculated to contrast with palette.primary.main
    },
    text: {
      primary: "",
    },
  },
});

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  fontSize: "14px",
  [`& .${gridClasses.columnHeaders}`]: {
    backgroundColor: "#FFF",
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: "none",
    "&:hover, &.Mui-hovered": {
      backgroundColor: themeChange.palette.primary.main,
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    fontSize: "14px",
    backgroundColor: "#FFF",
    "&:hover, &.Mui-hovered": {
      backgroundColor: themeChange.palette.primary.main,
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
}));

export const dateValueFormatter = (params: GridValueFormatterParams) => {
  const { value = "" } = params;
  return value && format(parseISO(String(value)), "dd/MMM/yyyy hh:mm:ss 'A'");
};

export const DashboardTable: FC<DashboardTableProps> = (props, history) => {
  const { labels, rows, deleteDashboard } = props;
  const [loading, setLoading] = useState(false);
  const [sortedData, setSortedData] = useState([] as Array<Dashboard>);
  const [pageSize, setPageSize] = useState(30 as number);
  const [rowData, setrowData] = useState({} as Dashboard);
  const [anchorEL, setAnchorEL] = useState<null | HTMLElement>(null);
  const openIndividualMenu = Boolean(anchorEL);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  useEffect(() => {
    setSortedData(rows);
    setLoading(true);
    setAnchorEL(null);
  }, [rows]);

  useEffect(() => {
    if (debouncedSearchValue) {
      const filteredData:Array<Dashboard> = rows.filter((row) => {
        return (
          row.title
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.description
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          row.lastModifiedString
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase())
        );
      });
      setSortedData(filteredData);
    } else {
      setSortedData(rows);
    }
  }, [debouncedSearchValue]);

  const theme2 = createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
    },
  });

  const handleActionClick = (event, rowValue) => {
    setAnchorEL(event.currentTarget);
    setrowData(rowValue);
  };

  const handleIndividualClose = () => {
    setAnchorEL(null);
  };

  const handleIndividualActionClick = (event, action) => {
    event.stopPropagation();
    if (action === "edit") {
      OnPremiseRedirectToEdit(rowData.id);
      setAnchorEL(null);
    }

    if (action === "delete") {
      deleteDashboard(rowData.id);
      setAnchorEL(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "action",
      headerName: "",
      width: 56,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { row = {} } = params;
        return <TextInput type="checkBox" />;
      },
    },
    {
      field: "title",
      sortable: true,
      headerName: labels["Dashboard.Name.Text"] || "Name",
      width: 256,
    },
    {
      // sorting on a date string will never work so we take in the raw data here and return the string
      field: "lastModified",
      headerName: labels["LastModified.Text"],
      sortable: true,
      width: 256,
      renderCell: (params: GridCellParams) => {
        const { row = {} } = params;
        return row.lastModifiedString;
      },
    },
    {
      field: "description",
      headerName: labels["Description.Text"],
      sortable: true,
      width: 256,
    },
    {
      field: "verticalButton",
      headerName: "",
      width: 56,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { row = {} } = params;
        return (
          <>
            <IconButton
              id="individual-actions"
              aria-haspopup="true"
              onClick={(event) => {
                handleActionClick(event, row);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEL}
              open={openIndividualMenu}
              onClose={handleIndividualClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  border: `1px solid ${PRIMARY_BLUE}`,
                  overflow: "visible",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "center" }}
            >
              <MenuItem
                key={1}
                onClick={(event) => {
                  handleIndividualActionClick(event, "edit");
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                key={2}
                onClick={(event) => {
                  handleIndividualActionClick(event, "delete");
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];


  const OnPremiseRedirectToEdit = (id: number) => {
    const url = window.location.href.slice(
      0,
      window.location.href.indexOf("React")
    );
    window.location.href =
      url + `DataVisualization/Dashboard.aspx?DashId=${id}`;
  };

  const OnPremiseRedirectToActionSettings = () => {
    const url = window.location.href.slice(
      0,
      window.location.href.indexOf("React")
    );
    window.location.href = url + "DataVisualization/Dashboard.aspx";
  };

  return (
    <Grid
    id="mgmDashTop"
      container
      sx={{
        display: "flex",
        width: "calc(100% - 40px)",
        padding: "20px",
      }}
      spacing={2}
    >
      <Grid item xs={8}>
        <Box
          sx={{
            display: "flex",
            paddingTop: "10px",
          }}
          justifyContent="flex-start"
        >
          <Typography
            sx={{
              color: `${DARK_GREY}`,
              fontSize: "20px",
              fontWeight: "bold !important",
              fontFamily: "Lato,Helvetica Neue,Arial,Helvetica,sans-serif",
            }}
            variant="h1"
          >
            Manage Dashboard
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={4}>
        <Box
          sx={{
            display: "flex",
          }}
          justifyContent="flex-end"
        >
          <ThemeProvider theme={theme2}>
            <ButtonStyles
              id="diag_save_button"
              variant="contained"
              onClick={(e) => {
                OnPremiseRedirectToActionSettings();
              }}
              startIcon={<AddIcon sx={{ marginRight: "0px !important" }} />}
            >
              Create Dashboard
            </ButtonStyles>
          </ThemeProvider>
        </Box>
      </Grid>
      <Grid item xs={4} justifyContent="start" sx={{ height: "auto" }}>
        <FormControl variant="standard">
          <OutlinedInput
            type="text"
            placeholder="Search"
            sx={InputField}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon sx={{ paddingRight: "5px" }} />
              </InputAdornment>
            }
            inputProps={{
              style: {
                border: "none",
                paddingLeft: "5px",
              },
            }}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <>
          <StripedDataGrid
            sx={{
              ".MuiTablePagination-displayedRows": {
                "margin-top": "1em",
                "margin-bottom": "1em",
              },
              ".MuiTablePagination-selectLabel": {
                "margin-top": "1em",
                "margin-bottom": "1em",
              },
            }}
            rowHeight={56}
            rows={sortedData}
            columns={columns}
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            autoHeight={true}
            getRowClassName={(params) =>
              (params as any).indexRelativeToCurrentPage % 2 === 0
                ? "even"
                : "odd"
            }
            components={{ Pagination: CustomPagination }}
          />
        </>
      </Grid>
    </Grid>
  );
};

export default DashboardTable;
